import { inject } from 'vue'

type Place = 'right' | 'bottom' | 'left'
export interface CcTip { showTip: (e: Event, text: string, place?: Place) => void; hideTip: () => void }

/**
 * AppLayout's tooltip (the one the sidebar uses) as attrs for v-bind: `v-bind="tipOn('…')"`.
 * Hover and focus show it, leave/blur/click hide it. Outside the layout (unit tests) the native title is the fallback.
 */
// Sweep only: hover waits a beat so tips don't flash while the pointer crosses dense rows. Keyboard focus stays instant.
const HOVER_DELAY_MS = 400
let timer: ReturnType<typeof setTimeout> | undefined
export const tipAttrs = (t: CcTip | null | undefined, text: string | null | undefined, place: Place = 'bottom', delay = HOVER_DELAY_MS) => {
  if (!text) return {}
  if (!t) return { title: text }
  const hide = () => { clearTimeout(timer); t.hideTip() }
  // No tip over a trigger whose own menu is open.
  const menuOpen = (el: Element) => el.getAttribute('aria-expanded') === 'true'
  return {
    // showTip reads e.currentTarget, which is null once the event is done, so hand it the element.
    onMouseenter: (e: Event) => { const el = e.currentTarget as Element | null; clearTimeout(timer); timer = setTimeout(() => { if (el?.isConnected && !menuOpen(el)) t.showTip({ currentTarget: el } as unknown as Event, text, place) }, delay) },
    onFocusin: (e: Event) => { clearTimeout(timer); if (!menuOpen(e.currentTarget as Element)) t.showTip(e, text, place) },
    onMouseleave: hide, onFocusout: hide, onClick: hide,
  }
}

/** For components rendered inside AppLayout. A component that renders AppLayout (or SweepShell) itself uses their `ccTip` slot prop with tipAttrs. */
export function useTip() {
  const t = inject<CcTip | null>('ccTip', null)
  return (text: string | null | undefined, place: Place = 'bottom', delay?: number) => tipAttrs(t, text, place, delay)
}
